import React, { useCallback, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import AsyncState from '../../components/AsyncState';
import LifecycleShell from '../../components/LifecycleShell';
import StatusBadge from '../../components/StatusBadge';
import StatusStepper from '../../components/StatusStepper';
import { useCurrentUser } from '../../contexts/CurrentUserContext';
import { useLanguage } from '../../contexts/LanguageContext';
import { useApi } from '../../hooks/useApi';
import { translations } from '../../translations';
import { fillTemplate } from '../../utils/format';
import ActivityPanel from './tabs/ActivityPanel';
import AssetsTab from './tabs/AssetsTab';
import BuildTab from './tabs/BuildTab';
import DiscussionTab from './tabs/DiscussionTab';
import OverviewTab from './tabs/OverviewTab';
import PublicationTab from './tabs/PublicationTab';

const POLL_MS = 30000;

export default function CustomAppDetailPage() {
  const { id } = useParams();
  const location = useLocation();
  const { language } = useLanguage();
  const t = translations[language];
  const { can } = useCurrentUser();
  const [tab, setTab] = useState('overview');
  const base = `/custom-apps/${encodeURIComponent(id)}`;
  const app = useApi(base, { pollMs: POLL_MS });
  const assets = useApi(`${base}/assets`, { pollMs: POLL_MS });
  const comments = useApi(`${base}/comments`, { pollMs: POLL_MS });
  const timeline = useApi(`${base}/timeline`, { pollMs: POLL_MS });

  // A change on one tab can alter the header, the attention flags and the activity feed.
  const refreshAll = useCallback(() => Promise.all([app.refresh(), assets.refresh(), comments.refresh(), timeline.refresh()]), [app, assets, comments, timeline]);

  const item = app.data;
  const tabs = [
    { key: 'overview', label: t.tabOverview },
    { key: 'assets', label: t.tabAssets },
    { key: 'discussion', label: t.tabDiscussion },
    ...(can('mapping:manage') ? [{ key: 'build', label: t.tabBuild }] : []),
    { key: 'publication', label: t.tabPublication },
  ];
  const uploadFailures = location.state?.uploadFailures;

  // Tabs follow the ARIA pattern: one tab stop, arrow keys move between them.
  const moveBetweenTabs = (event) => {
    const keys = tabs.map(({ key }) => key);
    const at = keys.indexOf(tab);
    const next = {
      ArrowRight: keys[(at + 1) % keys.length],
      ArrowLeft: keys[(at - 1 + keys.length) % keys.length],
      Home: keys[0],
      End: keys[keys.length - 1],
    }[event.key];
    if (!next) return;
    event.preventDefault();
    setTab(next);
    document.getElementById(`tab-${next}`)?.focus();
  };

  const title = item?.appName || item?.appInfo?.appName || t.untitledApp;
  const trail = [{ label: t.listTitle, to: '/custom-apps' }, { label: title }];

  return <LifecycleShell title={title} eyebrow={item?.company?.name} trail={trail}>
    <AsyncState state={app} retry={app.retry}>
      {item && <>
        {uploadFailures?.length > 0 && <div className="notice notice--warn" role="alert">{fillTemplate(t.filesFailedNotice, { files: uploadFailures.join(', ') })}</div>}
        <section className="card stack" aria-label={t.statusSection}>
          <div className="cluster">
            <StatusBadge status={item.status} />
            {(item.attentionReasons || []).map((reason) => <span className="chip chip--warn" key={reason}>{t.reasonLabel[reason] || reason}</span>)}
          </div>
          <StatusStepper progress={item.progress} />
        </section>

        <div className="split split--aside">
          <div className="stack">
            <div className="segmented" role="tablist" aria-label={title}>
              {tabs.map(({ key, label }) => <button
                key={key}
                id={`tab-${key}`}
                type="button"
                role="tab"
                aria-selected={tab === key}
                aria-controls={`panel-${key}`}
                tabIndex={tab === key ? 0 : -1}
                className="segmented__item"
                onClick={() => setTab(key)}
                onKeyDown={moveBetweenTabs}
              >{label}</button>)}
            </div>

            <div id={`panel-${tab}`} role="tabpanel" aria-labelledby={`tab-${tab}`}>
              {tab === 'overview' && <OverviewTab app={item} onChanged={refreshAll} />}
              {tab === 'assets' && <AssetsTab appId={item.id} assets={assets} canUpload={can('asset:upload')} onChanged={refreshAll} />}
              {tab === 'discussion' && <DiscussionTab appId={item.id} comments={comments} canWrite={can('comment:write')} onChanged={refreshAll} />}
              {tab === 'build' && can('mapping:manage') && <BuildTab app={item} onChanged={refreshAll} />}
              {tab === 'publication' && <PublicationTab key={`${item.status}|${JSON.stringify(item.publicationSummary)}`} app={item} canEdit={can('publication:edit')} onChanged={refreshAll} />}
            </div>
          </div>
          <ActivityPanel timeline={timeline} />
        </div>
      </>}
    </AsyncState>
  </LifecycleShell>;
}
